import { z } from 'zod';
import { uuidSchema } from './common';

// =============================================================================
// REQUEST SCHEMAS
// =============================================================================

// --- Param schemas ---

export const adminOrgIdParam = z.object({
  orgId: uuidSchema,
});

export const adminOrgAdminIdParam = z.object({
  orgId: uuidSchema,
  adminId: uuidSchema,
});

export const adminUserIdParam = z.object({
  userId: uuidSchema,
});

export const adminPlanIdParam = z.object({
  planId: uuidSchema,
});

/** PATCH /api/admin/hubs/:hubId/harness — hub-layer rollout gate (harness-agents PR 2.9a-2). */
export const adminHubIdParam = z.object({
  hubId: uuidSchema,
});

// --- Query schemas ---

export const adminOrganizationsQuery = z.object({
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(100).default(20),
  search: z.string().optional().default(''),
});

export const adminUserSearchQuery = z.object({
  email: z.string().min(3, 'email query parameter must be at least 3 characters'),
});

// One-time kanban slug migration query. Default is dry-run for safety.
export const kanbanSlugMigrationQuery = z.object({
  dry_run: z.enum(['true', 'false']).optional(),
});

// --- Body schemas ---

export const adjustQuotaBody = z.object({
  metric_type: z.string().min(1, 'metric_type is required'),
  action: z.enum(['add', 'set']),
  value: z.number().int().min(0, 'value must be a non-negative number'),
});

export const updatePlanBody = z.object({
  plan_type: z.enum(['free', 'paid']),
});

export const addAdminUserBody = z.object({
  user_email: z.string().email('user_email must be a valid email address'),
});

export const updatePlatformConfigBody = z.object({
  free_plan_max_free_orgs_per_user: z.number().int().min(0).optional(),
  /** Kill switch for admin debug-read endpoints. 0 = off, 1 = on. */
  debug_read_enabled: z.number().int().min(0).max(1).optional(),
  /**
   * Harness-agents rollout gate + kill switch (harness-agents PR 2.9a/2.9b). 0 = off
   * (default-deny — the per-turn kill switch aborts every harness turn on its next
   * invocation), 1 = on. `wayai admin harness disable|enable` flips this. The DO's
   * `updatePlatformConfig` allowlist already accepts it; this exposes it on the route.
   */
  harness_enabled: z.number().int().min(0).max(1).optional(),
}).strip().refine(
  (data) => Object.keys(data).length > 0,
  { message: 'No fields to update' }
);

export const updateFreeOrgLimitBody = z.object({
  max_free_orgs_override: z.number().int().min(0).nullable(),
});

/**
 * PATCH /api/admin/organizations/:orgId/harness — flip the ORG-layer harness
 * rollout gate (harness-agents PR 2.9a-2). The middle of the three-layer flag
 * (platform AND org AND hub) enforced by the HubDO create/update guard. 0 = deny
 * (default), 1 = allow. Fail-closed everywhere; staged rollout only.
 */
export const adminSetOrgHarnessBody = z.object({
  harness_enabled: z.number().int().min(0).max(1),
}).strict();

/**
 * PATCH /api/admin/hubs/:hubId/harness — flip the HUB-layer harness rollout gate
 * (harness-agents PR 2.9a-2). The innermost of the three-layer flag. 0 = deny
 * (default), 1 = allow.
 */
export const adminSetHubHarnessBody = z.object({
  harness_enabled: z.number().int().min(0).max(1),
}).strict();

/**
 * POST /api/admin/harness/mass-destroy — the operator incident-response reap
 * (harness-agents PR 2.9b). Enumerates live harness sandboxes/slots in `scope`
 * and reaps each conversation's sandbox + MCP token + concurrency slot on demand.
 * Complements the automated per-turn kill switch (PR 2.9a), which only fires when
 * a conversation next turns; this reclaims what is live RIGHT NOW.
 *
 * Exactly ONE id must match `scope` (enforced by the superRefine): `org` needs
 * `org_id` (and no `hub_id`); `hub` needs `hub_id` (and no `org_id`); `all` takes
 * neither. `dry_run` reports the blast radius (sessions_found) without reaping —
 * run it first. `.strict()` rejects unknown keys so a typo'd scope-key can't widen
 * the target.
 */
export const adminHarnessMassDestroyBody = z
  .object({
    scope: z.enum(['all', 'org', 'hub']),
    org_id: z.string().uuid().optional(),
    hub_id: z.string().optional(),
    dry_run: z.boolean().optional().default(false),
  })
  .strict()
  .superRefine((data, ctx) => {
    if (data.scope === 'org') {
      if (!data.org_id) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'scope=org requires org_id', path: ['org_id'] });
      }
      if (data.hub_id) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'scope=org must not include hub_id', path: ['hub_id'] });
      }
    } else if (data.scope === 'hub') {
      if (!data.hub_id) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'scope=hub requires hub_id', path: ['hub_id'] });
      }
      if (data.org_id) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'scope=hub must not include org_id', path: ['org_id'] });
      }
    } else {
      // scope === 'all' — platform-wide, so neither id is allowed.
      if (data.org_id) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'scope=all must not include org_id', path: ['org_id'] });
      }
      if (data.hub_id) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'scope=all must not include hub_id', path: ['hub_id'] });
      }
    }
  });

export const updatePricingPlanBody = z.object({
  display_name: z.string().min(1).optional(),
  stripe_product_id: z.string().optional(),
  lookup_key_prefix: z.string().optional(),
  messages_per_unit: z.number().int().min(1, 'messages_per_unit must be a positive integer').optional(),
  seats_per_unit: z.number().int().min(1, 'seats_per_unit must be a positive integer').optional(),
  seats_scaling: z.enum(['fixed', 'per_quantity']).optional(),
  is_active: z.boolean().optional(),
}).strip().refine(
  (data) => Object.keys(data).length > 0,
  { message: 'No fields to update' }
);

// =============================================================================
// INFERRED TYPES
// =============================================================================

export type AdjustQuotaInput = z.infer<typeof adjustQuotaBody>;
export type UpdatePlanInput = z.infer<typeof updatePlanBody>;
export type AddAdminUserInput = z.infer<typeof addAdminUserBody>;
export type UpdatePlatformConfigInput = z.infer<typeof updatePlatformConfigBody>;
export type UpdateFreeOrgLimitInput = z.infer<typeof updateFreeOrgLimitBody>;
export type UpdatePricingPlanInput = z.infer<typeof updatePricingPlanBody>;
export type AdminHarnessMassDestroyInput = z.infer<typeof adminHarnessMassDestroyBody>;
export type AdminSetOrgHarnessInput = z.infer<typeof adminSetOrgHarnessBody>;
export type AdminSetHubHarnessInput = z.infer<typeof adminSetHubHarnessBody>;

// =============================================================================
// RESPONSE TYPES
// =============================================================================

/** GET /admin/check */
export interface AdminCheckResponse {
  is_platform_admin: true;
}

/** Billing metric row returned inside organization list/detail */
export interface AdminBillingMetric {
  organization_id: string;
  metric_type: string;
  monthly_included: number;
  consumed_subscription: number;
}

/** Organization item in the paginated list */
export interface AdminOrganizationItem {
  organization_id: string;
  organization_name: string;
  created_at: string;
  plan_type: string;
  billing_status: string;
  has_subscription: boolean;
  period_end: string | null;
  owner_email: string | null;
  admin_users: Array<{
    organization_admin_user_id: string;
    user_email: string;
    is_owner: boolean;
  }>;
  metrics: AdminBillingMetric[];
}

/** Pagination metadata */
export interface AdminPaginationMeta {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

/** GET /admin/organizations */
export interface AdminOrganizationsListResponse {
  data: AdminOrganizationItem[];
  pagination: AdminPaginationMeta;
}

/** POST /admin/organizations/:orgId/resync-registry */
export interface AdminResyncOrgRegistryResponse {
  ok: true;
  org_id: string;
  patched_fields: string[];
}

/** POST /admin/dev/backfill-org-registry */
export interface AdminBackfillOrgRegistryResponse {
  processed: number;
  succeeded: number;
  failed: number;
  failures: Array<{ org_id: string; error: string }>;
}

/** GET /admin/organizations/:orgId */
export interface AdminOrganizationDetailResponse {
  data: {
    organization_id: string;
    billing: Record<string, unknown> | null;
    metrics: AdminBillingMetric[];
    admin_users: Array<{
      organization_admin_user_id: string;
      user_email: string;
      is_owner: boolean;
    }>;
  };
}

/** POST /admin/organizations/:orgId/admins */
export interface AdminAddAdminUserResponse {
  data: {
    organization_admin_user_id: string;
    user_email: string;
    is_owner: boolean;
  };
}

/** GET /admin/config, PATCH /admin/config */
export interface AdminConfigResponse {
  data: Record<string, unknown>;
}

/** User search result item */
export interface AdminUserSearchItem {
  user_id: string;
  user_email: string;
  profile_name: string | null;
  max_free_orgs_override: number | null;
}

/** GET /admin/users/search */
export interface AdminUserSearchResponse {
  data: AdminUserSearchItem[];
}

/** PATCH /admin/users/:userId/free-org-limit */
export interface AdminUpdateFreeOrgLimitResponse {
  data: AdminUserSearchItem;
}

/** PATCH /admin/organizations/:orgId/harness — echoes the persisted org-layer flag. */
export interface AdminSetOrgHarnessResponse {
  data: {
    organization_id: string;
    harness_enabled: number;
  };
}

/** PATCH /admin/hubs/:hubId/harness — echoes the persisted hub-layer flag. */
export interface AdminSetHubHarnessResponse {
  data: {
    hub_id: string;
    harness_enabled: number;
  };
}

/** Per-conversation reap outcome inside the mass-destroy response. NEVER carries tokens/secrets. */
export interface AdminHarnessMassDestroyDetail {
  hub_id: string;
  conversation_id: string;
  /** True when the slot/session had a live sandbox_id (a sandbox was in flight). */
  had_sandbox: boolean;
  /** True when the sandbox destroy succeeded (false ⇒ best-effort reap failed; cron backstop reclaims). */
  sandbox_destroyed: boolean;
  /** True when the harness namespace was present and reaped (false on a dry_run or an idempotent no-op). */
  reaped: boolean;
}

/** POST /admin/harness/mass-destroy — aggregate reap report. */
export interface AdminHarnessMassDestroyResponse {
  data: {
    scope: 'all' | 'org' | 'hub';
    dry_run: boolean;
    /** Distinct hubs scanned for live harness slots. */
    hubs_scanned: number;
    /** Hubs whose slot enumeration threw and were skipped (partial-failure signal — re-run to cover them). */
    hubs_errored: number;
    /** Live harness sessions discovered across all successfully-scanned hubs. */
    sessions_found: number;
    /** Sessions actually reaped (0 on a dry_run). */
    sessions_reaped: number;
    /** Sessions whose reap RPC threw and were skipped (0 on a dry_run; re-run to cover them). */
    sessions_errored: number;
    /** Sandboxes successfully destroyed (0 on a dry_run). */
    sandboxes_destroyed: number;
    details: AdminHarnessMassDestroyDetail[];
  };
}

/** GET /admin/pricing-plans */
export interface AdminPricingPlansListResponse {
  data: Array<Record<string, unknown>>;
}

/** PATCH /admin/pricing-plans/:planId */
export interface AdminUpdatePricingPlanResponse {
  data: Record<string, unknown>;
}

// One-time kanban slug migration result.
export interface KanbanSlugMigrationResult {
  dry_run: boolean;
  total_hubs: number;
  migrated: number;
  already_migrated: number;
  skipped: Array<{ hub_id: string; reason: string }>;
}

/** POST /admin/migrate/kanban-slugs */
export interface KanbanSlugMigrationResponse {
  data: KanbanSlugMigrationResult;
}

// =============================================================================
// DATA EXPLORER — Request Schemas
// =============================================================================

export const dataExplorerSearchQuery = z.object({
  q: z.string().min(1, 'search query is required'),
  type: z.enum(['org', 'hub', 'user']).optional(),
});

export const dataExplorerOrgIdParam = z.object({ orgId: uuidSchema });
export const dataExplorerHubIdParam = z.object({ hubId: uuidSchema });
export const dataExplorerConvIdParam = z.object({ convId: uuidSchema });

export const dataExplorerDoInstancesQuery = z.object({
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
  orphans_only: z.coerce.boolean().optional().default(false),
  /** Direct lookup by entity UUID (hub_id/org_id/user_id/conversation_id). When set, bypasses pagination and returns a synthesized entry via idFromName. */
  search: z.string().optional(),
});

export const dataExplorerNsIdParam = z.object({
  nsId: z.string().min(1, 'namespace ID is required'),
});

export const dataExplorerKvKeysQuery = z.object({
  prefix: z.string().optional(),
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
});

export const dataExplorerKvKeyParam = z.object({
  nsId: z.string().min(1),
  key: z.string().min(1, 'key is required'),
});

/** Accepts UUID or Cloudflare DO hex ID (for orphan deletion) */
const uuidOrHexId = z.string().min(1);
export const dataExplorerDeleteOrgParam = z.object({ orgId: uuidOrHexId });
export const dataExplorerDeleteHubParam = z.object({ hubId: uuidOrHexId });
export const dataExplorerDeleteConvParam = z.object({ convId: uuidOrHexId });
export const dataExplorerDeleteUserParam = z.object({ userId: uuidOrHexId });

export const dataExplorerDeleteQuery = z.object({
  confirmation: z.string().min(1, 'confirmation is required'),
  cascade: z.coerce.boolean().optional().default(false),
});

export const dataExplorerKvDeleteQuery = z.object({
  confirmation: z.string().min(1, 'confirmation is required'),
});

// =============================================================================
// DATA EXPLORER — Inferred Types
// =============================================================================

export type DataExplorerSearchInput = z.infer<typeof dataExplorerSearchQuery>;
export type DataExplorerDeleteInput = z.infer<typeof dataExplorerDeleteQuery>;

// =============================================================================
// DATA EXPLORER — Response Types
// =============================================================================

/** Hub entry in org entity tree */
export interface DataExplorerHubEntry {
  hub_id: string;
  hub_name: string;
  hub_type: string;
  hub_environment: string;
  production_hub_id: string | null;
  dashboard_url: string | null;
}

/** Search result item */
export interface DataExplorerSearchResultItem {
  type: 'org' | 'hub' | 'user';
  id: string;
  name: string;
  /** Extra context (e.g., org name for hub results, email for user results) */
  context?: string;
}

/** GET /data-explorer/search */
export interface DataExplorerSearchResponse {
  data: DataExplorerSearchResultItem[];
}

/** GET /data-explorer/entity/org/:orgId */
export interface DataExplorerOrgEntityResponse {
  data: {
    organization_id: string;
    organization_name: string;
    dashboard_url: string | null;
    hubs: DataExplorerHubEntry[];
    billing: Record<string, unknown> | null;
  };
}

/** GET /data-explorer/entity/hub/:hubId */
export interface DataExplorerHubEntityResponse {
  data: {
    hub_id: string;
    hub_name: string;
    hub_type: string;
    dashboard_url: string | null;
    conversation_count: number;
  };
}

/** GET /data-explorer/entity/conversation/:convId */
export interface DataExplorerConvEntityResponse {
  data: {
    conversation_id: string;
    dashboard_url: string | null;
    conversation: Record<string, unknown> | null;
  };
}

/** DO namespace item */
export interface DataExplorerDoNamespace {
  id: string;
  name: string;
  class: string;
}

/** GET /data-explorer/do/namespaces */
export interface DataExplorerDoNamespacesResponse {
  data: DataExplorerDoNamespace[];
}

/** DO instance item */
export interface DataExplorerDoInstance {
  id: string;
  name: string | null;
  /** `null` means the backend did not probe CF for storage state (e.g., direct search by UUID) */
  hasStoredData: boolean | null;
  dashboard_url: string;
  /** The domain entity ID (e.g., org_id, hub_id) used in idFromName — null if unknown */
  entity_id?: string | null;
  /** Resolved domain name (e.g., org name) — null if not resolved */
  resolved_name?: string | null;
  /** Whether this instance is an orphan (not in any known hierarchy) */
  is_orphan?: boolean;
}

/** GET /data-explorer/do/namespaces/:nsId/instances */
export interface DataExplorerDoInstancesResponse {
  data: DataExplorerDoInstance[];
  cursor?: string;
}

/** KV namespace item */
export interface DataExplorerKvNamespace {
  name: string;
  id: string;
}

/** GET /data-explorer/kv/namespaces */
export interface DataExplorerKvNamespacesResponse {
  data: DataExplorerKvNamespace[];
}

/** KV key item */
export interface DataExplorerKvKeyItem {
  name: string;
  metadata: Record<string, unknown> | null;
  expiration?: number;
}

/** GET /data-explorer/kv/namespaces/:nsId/keys */
export interface DataExplorerKvKeysResponse {
  data: DataExplorerKvKeyItem[];
  cursor?: string;
}

/** GET /data-explorer/kv/namespaces/:nsId/keys/:key */
export interface DataExplorerKvValueResponse {
  data: {
    key: string;
    value: string | null;
    masked: boolean;
  };
}

/** DELETE responses */
export interface DataExplorerDeleteResult {
  deleted: string[];
  failed: Array<{ id: string; error: string }>;
}

export interface DataExplorerDeleteResponse {
  data: DataExplorerDeleteResult;
}

// =============================================================================
// PITR (Point-in-Time Recovery) — Request Schemas
// =============================================================================

/** Valid DO namespace names for PITR operations */
export const pitrDoNamespace = z.enum(['HUB_DO', 'CONVERSATION_DO', 'ORG_DO', 'USER_DO', 'PLATFORM_DO']);

/** GET /admin/pitr/:namespace/:instanceId/bookmark */
export const pitrBookmarkParam = z.object({
  namespace: pitrDoNamespace,
  instanceId: z.string().min(1, 'instanceId is required'),
});

/** POST /admin/pitr/:namespace/:instanceId/restore — reuses bookmark param shape */
export const pitrRestoreParam = pitrBookmarkParam;

/** Body for restore request */
export const pitrRestoreBody = z.object({
  /** ISO 8601 timestamp to restore to (must be within last 30 days) */
  target_timestamp: z.string().datetime('target_timestamp must be a valid ISO 8601 datetime'),
  /** Must match the instanceId for confirmation */
  confirmation: z.string().min(1, 'confirmation is required'),
  /** If true, only fetch the bookmark without performing the restore (default: true) */
  dry_run: z.boolean().default(true),
});

// =============================================================================
// PITR — Inferred Types
// =============================================================================

export type PitrDoNamespace = z.infer<typeof pitrDoNamespace>;
export type PitrRestoreInput = z.infer<typeof pitrRestoreBody>;

// =============================================================================
// PITR — Response Types
// =============================================================================

/** GET /admin/pitr/:namespace/:instanceId/bookmark */
export interface PitrBookmarkResponse {
  data: {
    namespace: string;
    instance_id: string;
    current_bookmark: string;
  };
}

/** POST /admin/pitr/:namespace/:instanceId/restore */
export interface PitrRestoreResponse {
  data: {
    namespace: string;
    instance_id: string;
    target_timestamp: string;
    target_bookmark: string;
    current_bookmark: string;
    /** Bookmark that can be used to undo this restore */
    undo_bookmark: string | null;
    dry_run: boolean;
    restore_scheduled: boolean;
  };
}

// =============================================================================
// DO Health Validation — Response Types
// =============================================================================

export interface HealthValidationIssue {
  table: string;
  check: 'existence' | 'row_count' | 'json_validity';
  severity: 'error' | 'warning';
  message: string;
}

export interface HealthValidationResponse {
  data: {
    status: 'pass' | 'fail';
    tables_checked: number;
    tables_passed: number;
    issues: HealthValidationIssue[];
    checked_at: string;
  };
}

// =============================================================================
// Health Sampling — Request Schemas
// =============================================================================

/** POST /admin/health-sampling — trigger body */
export const healthSamplingTriggerBody = z.object({
  /** Number of instances to sample per DO type (default: 10) */
  sample_size: z.number().int().min(1).max(100).default(10),
  /** Filter to specific DO types (default: all) */
  do_types: z.array(pitrDoNamespace).optional(),
});

export type HealthSamplingTriggerInput = z.infer<typeof healthSamplingTriggerBody>;

// =============================================================================
// DATA EXPLORER DEBUG READS — Request Schemas
// =============================================================================
//
// Platform-admin-only read-only inspection of DO SQLite state. Gated by
// platformAdminMiddleware + strict rate limit + kill switch + column redaction.
// See workers/backend/src/durable-objects/debug-redaction.ts for the central
// allowlist and redaction rules.

/** Valid DO types for debug reads. Shared source of truth — re-exported as `DoType` in debug-redaction.ts. */
export const dataExplorerDebugDoType = z.enum(['hub', 'org', 'conversation', 'user', 'platform']);

/** Hard cap on `limit` query param — applied in both Zod and the DO RPC. */
export const DEBUG_READ_MAX_LIMIT = 100;
/** Default `limit` when the caller omits it. */
export const DEBUG_READ_DEFAULT_LIMIT = 20;

export const DEBUG_DO_HEX_PREFIX = 'hex:';

/**
 * `hex:<64-hex>` is resolved via `idFromString` (raw CF DO id — for inspecting
 * DOs discovered without a reverse path, e.g. orphan-audit). All other forms
 * go through `idFromName`.
 */
const debugDoEntityId = z
  .string()
  .min(1, 'entity id is required')
  .max(68)
  .refine(
    (v) => !v.startsWith(DEBUG_DO_HEX_PREFIX) || /^hex:[0-9a-f]{64}$/.test(v),
    { message: 'hex: prefix must be followed by exactly 64 lowercase hex chars' },
  );

/**
 * Table name param. Two-layer injection defense: regex here + assertTableAllowed()
 * against a frozen allowlist in the DO RPC. Table names are NEVER string-interpolated
 * into SQL — they're looked up in a constant map.
 */
const debugTableName = z.string().regex(
  /^[a-z][a-z0-9_]{0,63}$/,
  'table name must be lowercase letters, digits, underscores (<=64 chars)',
);

/** GET /api/admin/data-explorer/debug/do/:type/:id/tables */
export const dataExplorerDebugTablesParam = z.object({
  type: dataExplorerDebugDoType,
  id: debugDoEntityId,
});

/** GET /api/admin/data-explorer/debug/do/:type/:id/tables/:name/rows */
export const dataExplorerDebugRowsParam = z.object({
  type: dataExplorerDebugDoType,
  id: debugDoEntityId,
  name: debugTableName,
});

export const dataExplorerDebugRowsQuery = z.object({
  limit: z.coerce.number().int().min(1).max(DEBUG_READ_MAX_LIMIT).default(DEBUG_READ_DEFAULT_LIMIT),
  /** Optional lookup by primary key value — typed as string since PKs are all TEXT UUIDs */
  id: z.string().min(1).max(256).optional(),
});

// =============================================================================
// DATA EXPLORER DEBUG READS — Inferred Types
// =============================================================================

export type DataExplorerDebugDoType = z.infer<typeof dataExplorerDebugDoType>;
export type DataExplorerDebugRowsQueryInput = z.infer<typeof dataExplorerDebugRowsQuery>;

// =============================================================================
// DATA EXPLORER DEBUG READS — Response Types
// =============================================================================

/** GET /api/admin/data-explorer/debug/do/:type/:id/tables */
export interface DataExplorerDebugTablesResponse {
  data: {
    do_type: DataExplorerDebugDoType;
    do_id: string;
    /** Tables present in the DO that are also in the debug allowlist */
    tables: string[];
  };
}

/** GET /api/admin/data-explorer/debug/do/:type/:id/tables/:name/rows */
export interface DataExplorerDebugRowsResponse {
  data: {
    do_type: DataExplorerDebugDoType;
    do_id: string;
    table: string;
    /** Rows with sensitive columns replaced by the string "[REDACTED]" */
    rows: Record<string, unknown>[];
    /** Column names that were redacted in this response (for operator awareness) */
    redacted_columns: string[];
    /** True if the result was capped at `limit` and more rows may exist */
    truncated: boolean;
    /** Applied limit — echoes the query param or its default */
    limit: number;
  };
}

// =============================================================================
// DATA EXPLORER DEBUG READS — ClickHouse analytics tables
// =============================================================================
//
// Same gates as the DO debug surface: platform-admin, audit, rate-limit, kill
// switch. CH tables are queried via parameterized SQL (`{key:Type}` placeholders)
// — never string-interpolated. Allowlist + redaction live in
// workers/backend/src/clickhouse/debug-redaction.ts.

/** Tables an admin can read via `wayai admin analytics`. */
export const dataExplorerDebugAnalyticsTable = z.enum([
  'conversation',
  'message',
  'billing_event',
  'schedule_event',
]);
export type DataExplorerDebugAnalyticsTable = z.infer<typeof dataExplorerDebugAnalyticsTable>;

/** UUID validator reused for filter values. Any non-UUID input is rejected before reaching CH. */
const debugUuid = z.string().uuid();

/** GET /api/admin/data-explorer/debug/analytics/:table/rows */
export const dataExplorerDebugAnalyticsRowsParam = z.object({
  table: dataExplorerDebugAnalyticsTable,
});

export const dataExplorerDebugAnalyticsRowsQuery = z.object({
  conversation_id: debugUuid.optional(),
  hub_id: debugUuid.optional(),
  message_id: debugUuid.optional(),
  limit: z.coerce.number().int().min(1).max(DEBUG_READ_MAX_LIMIT).default(DEBUG_READ_DEFAULT_LIMIT),
});

export type DataExplorerDebugAnalyticsRowsQueryInput = z.infer<typeof dataExplorerDebugAnalyticsRowsQuery>;

/** GET /api/admin/data-explorer/debug/analytics/tables */
export interface DataExplorerDebugAnalyticsTablesResponse {
  data: {
    tables: DataExplorerDebugAnalyticsTable[];
  };
}

/** GET /api/admin/data-explorer/debug/analytics/:table/rows */
export interface DataExplorerDebugAnalyticsRowsResponse {
  data: {
    table: DataExplorerDebugAnalyticsTable;
    rows: Record<string, unknown>[];
    /** Redacted column names or dotted JSON paths (e.g. "data", "data.meta.last_message_text"). */
    redacted_columns: string[];
    truncated: boolean;
    limit: number;
  };
}

// =============================================================================
// DATA EXPLORER DEBUG READS — R2 archive
// =============================================================================
//
// Admin-gated counterpart to `GET /api/archive/:hubId/conversations/:conversationId`.
// Same archive blob, but reachable only by platform admins and audited as
// `admin_debug_read`. Does not provision access — admins still need the blob to
// exist in R2.

// Hub+conversation pair shared by both debug-archive and debug-observability
// param schemas — keep them as one source of truth so route paths stay aligned.
const debugHubConvParam = z.object({
  hubId: debugUuid,
  conversationId: debugUuid,
});

// Optional message_id filter — same semantics for archive and observability
// (server returns the matching message; otherwise the full set).
const debugMessageIdQuery = z.object({
  message_id: debugUuid.optional(),
});

export const dataExplorerDebugArchiveParam = debugHubConvParam;
export const dataExplorerDebugArchiveQuery = debugMessageIdQuery;

export type DataExplorerDebugArchiveQueryInput = z.infer<typeof dataExplorerDebugArchiveQuery>;

/** GET /api/admin/data-explorer/debug/archive/:hubId/conversations/:conversationId */
export interface DataExplorerDebugArchiveResponse {
  data: {
    hub_id: string;
    conversation_id: string;
    /** The full archive blob written by the archive job, optionally with `messages` filtered server-side. */
    archive: Record<string, unknown>;
    /** True when ?message_id= filtered the messages array. */
    message_filtered: boolean;
  };
}

// =============================================================================
// DATA EXPLORER DEBUG READS — R2 LLM observability
// =============================================================================
//
// Admin-gated read of per-message OpenTelemetry GenAI records (full prompts,
// completions, tool calls, tokens, latency). The hub-scoped equivalent at
// `/api/conversations/:conversationId/...` enforces hub-user / team-member
// access; this debug variant requires `platform:admin` instead so an operator
// can inspect any hub for incident response. Same data shape both ways.

export const dataExplorerDebugObservabilityParam = debugHubConvParam;
export const dataExplorerDebugObservabilityQuery = debugMessageIdQuery;

export type DataExplorerDebugObservabilityQueryInput = z.infer<typeof dataExplorerDebugObservabilityQuery>;

/**
 * GET /api/admin/data-explorer/debug/observability/:hubId/conversations/:conversationId
 *
 * Discriminated by `kind` so TypeScript callers narrow without non-null assertions:
 * - `kind: 'list'` (no ?message_id) — `entries` is populated.
 * - `kind: 'single'` (with ?message_id) — `record` is populated.
 */
export type DataExplorerDebugObservabilityResponse =
  | {
      data: {
        kind: 'list';
        hub_id: string;
        conversation_id: string;
        entries: import('../observability/types').WayAIMetadataRecord[];
        /** True when the list was truncated by the server cap. */
        truncated: boolean;
      };
    }
  | {
      data: {
        kind: 'single';
        hub_id: string;
        conversation_id: string;
        record: import('../observability/types').LLMObservabilityRecord;
      };
    };

// =============================================================================
// Health Sampling — Response Types
// =============================================================================

export interface HealthSamplingInstanceResult {
  namespace: string;
  instance_id: string;
  status: 'pass' | 'fail' | 'error';
  issues: HealthValidationIssue[];
  error?: string;
}

export interface HealthSamplingReport {
  data: {
    status: 'pass' | 'fail';
    triggered_at: string;
    completed_at: string;
    commit?: string;
    sample_size: number;
    results: HealthSamplingInstanceResult[];
    summary: {
      total_instances: number;
      passed: number;
      failed: number;
      errors: number;
    };
  };
}
